import { useEffect, useRef, useState } from "react";
import { Navigate, Outlet } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ErrorBoundary from "../ErrorBoundary";
import { useAppSelector } from "../hooks/app.hooks";
import { useSubscribeMutation } from "../store/api";
import { Button } from "../fields/button";
import { appEnv } from "../config/env";
import styles from './style.module.scss';

const NOTIFICATION_SNOOZE_KEY = "notifications_prompt_snooze_until";

type PermissionState = "hidden" | "prompt" | "denied" | "unsupported";

function urlBase64ToUint8Array(base64String: string) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, "+")
    .replace(/_/g, "/");

  const rawData = window.atob(base64);
  return Uint8Array.from([...rawData].map((char) => char.charCodeAt(0)));
}

function isTelegramWebView() {
  return /Telegram/i.test(window.navigator.userAgent);
}

function isNotificationSnoozed() {
  const until = Number(localStorage.getItem(NOTIFICATION_SNOOZE_KEY) || "0");
  return Number.isFinite(until) && until > Date.now();
}

function snoozeNotificationPromptForDay() {
  const oneDayMs = 24 * 60 * 60 * 1000;
  localStorage.setItem(
    NOTIFICATION_SNOOZE_KEY,
    String(Date.now() + oneDayMs)
  );
}

function isPushUnsupported() {
  return (
    !("Notification" in window) ||
    !("serviceWorker" in navigator) ||
    !("PushManager" in window) ||
    isTelegramWebView()
  );
}

// Обработка клика по пушу
if ("serviceWorker" in navigator) {
  navigator.serviceWorker.addEventListener("message", (event) => {
    if (event.data && event.data.type === "NOTIFICATION_CLICK") {
      const { data } = event.data;

      const url =
        data.type.toLowerCase() === "task"
          ? `/task-create?ID_App=${data.params.ID}`
          : data.type.toLowerCase() === "fotorepvitr"
            ? `/photo-report-create?id=${data.params.ID}`
            : "/";

      window.location.href = url;
    }
  });
}

export const PrivateRouteMain = () => {
  const { isAuth, user } = useAppSelector((state) => state.auth);
  const [subscribe] = useSubscribeMutation();
  const [permissionState, setPermissionState] = useState<PermissionState>("hidden");
  const toastShownRef = useRef(false);

  // Проверяем статус при загрузке (без запроса permission!)
  useEffect(() => {
    if (isNotificationSnoozed()) return;

    if (isPushUnsupported()) {
      setPermissionState("unsupported");
      return;
    }

    if (Notification.permission === "granted") {
      createSubscribe();
      setPermissionState("hidden");
    } else if (Notification.permission === "default") {
      setPermissionState("prompt");
    } else if (Notification.permission === "denied") {
      setPermissionState("denied");
    }
  }, []);

  // 🔹 Запрос разрешения — ТОЛЬКО по клику
  const handleEnableNotifications = async () => {
    if (isPushUnsupported()) {
      setPermissionState("unsupported");
      return;
    }

    const permission = await Notification.requestPermission();

    if (permission === "granted") {
      await createSubscribe();
      setPermissionState("hidden");
    } else {
      setPermissionState("denied");
      if (!toastShownRef.current) {
        toast.info("Разрешение на уведомления отклонено");
        toastShownRef.current = true;
      }
    }
  };

  const handleSnooze = () => {
    snoozeNotificationPromptForDay();
    setPermissionState("hidden");
  };

  const createSubscribe = async () => {

    try {
      const registration = await navigator.serviceWorker.ready;
      const existingSubscription =
        await registration.pushManager.getSubscription();

      if (existingSubscription) return;

      if (!appEnv.pushPublicKey) return;

      const convertedVapidKey = urlBase64ToUint8Array(
        appEnv.pushPublicKey
      );

      const newSubscription =
        await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: convertedVapidKey,
        });

      await subscribe({
        newSubscription,
        id_con: user.notificationToken as string,
      }).unwrap();
    } catch (error) {
      // Не провоцируем бесконечный показ промпта в средах, где push недоступен.
      setPermissionState("hidden");
    }
  };

  if (!isAuth || !user.notificationToken) {
    return <Navigate to={"/login"} replace />;
  }

  const showPrompt = permissionState === "prompt";
  const showDeniedInfo = permissionState === "denied";
  const showUnsupportedInfo = permissionState === "unsupported";

  return (
    <ErrorBoundary>
      {showPrompt && (
        <div className={styles.notificationBox}>
          <p className={styles.notificationBoxText}>
            Включите уведомления для получения задач
          </p>
          <Button
            text={'Включить уведомления'}
            textStyleType="semiBold"
            style={{}}
            onClick={handleEnableNotifications} />
          <button
            type="button"
            onClick={handleSnooze}
            className={styles.dialogButton}
            style={{ marginTop: 8 }}
          >
            Не показывать сегодня
          </button>
        </div>
      )}

      {showDeniedInfo && (
        <div className={styles.notificationBox}>
          <p className={styles.notificationBoxText}>
            Уведомления отключены. Разрешите их в настройках браузера.
          </p>
          <button
            type="button"
            onClick={handleSnooze}
            className={styles.dialogButton}
          >
            Понятно
          </button>
        </div>
      )}

      {showUnsupportedInfo && (
        <div className={styles.notificationBox}>
          <p className={styles.notificationBoxText}>
            В этом браузере push-уведомления недоступны. Откройте приложение в Safari и добавьте на экран Домой.
          </p>
          <button
            type="button"
            onClick={handleSnooze}
            className={styles.dialogButton}
          >
            Понятно
          </button>
        </div>
      )}

      <Outlet />
    </ErrorBoundary>
  );
};